/**
 * Helpers for reading the raw 2026 feed. Components read the raw fields
 * directly and use these only for fields that need interpreting.
 *
 * @typedef {import('../types').Race} Race
 * @typedef {import('../types').Choice} Choice
 * @typedef {import('../types').CountyResult} CountyResult
 * @typedef {import('../types').TrendsFeed} TrendsFeed
 */

import parties from './parties';

const OFFICE_PREFIX = 'office-id-';
const SEAT_PREFIX = 'seat-';

/** @param {Race} race */
export const getTags = (race) => race.attributes.tags ?? [];

/** @param {Race} race @param {string} tag */
export const hasTag = (race, tag) => getTags(race).includes(tag);

/**
 * Office codes from the 'office-id-*' tags, e.g. ['Y']
 * @param {Race} race
 */
export const getOffices = (race) =>
  getTags(race)
    .filter((t) => t.startsWith(OFFICE_PREFIX))
    .map((t) => t.slice(OFFICE_PREFIX.length));

/** @param {Race} race @param {string} code */
export const hasOffice = (race, code) => getOffices(race).includes(code);

/**
 * District/seat number from the 'seat-*' tag ('seat-12' → 12), 0 if none.
 *
 * Only Assembly, State Senate and U.S. House races have the tag so far. The
 * rest fall back to the first number in jurisdiction: Board of Equalization
 * ('Seat 3'), local districts ('Oakland District 6') and propositions
 * ('1 - Authorize…', the prop number). Remove the fallback if the data team
 * adds seat tags to those races.
 * @param {Race} race
 */
export const getSeatNumber = (race) => {
  const tag = getTags(race).find((t) => t.startsWith(SEAT_PREFIX));
  const seat =
    tag?.slice(SEAT_PREFIX.length) ??
    race.attributes.jurisdiction?.match(/\d+/)?.[0];
  return parseInt(seat ?? '0', 10);
};

/** Propositions and local measures @param {Race} race */
export const isMeasure = (race) => race.attributes.electionType !== 'race';

/** @param {Race} race */
export const isProposition = (race) =>
  race.attributes.electionType === 'proposition';

/**
 * measurePassRate wording from the feed → what we show; '' hides it
 * @type {Record<string, string>}
 */
const PASS_RATE_NOTES = {
  'Majority of votes cast': '',
  '2/3 of votes cast': 'Two-thirds of votes needed',
  '55% of votes cast': '55% required to pass',
};

/**
 * The note on how a measure passes, or '' when there's nothing to show
 * (majority, or no measurePassRate). Wording we haven't mapped yet is shown
 * as-is so it isn't hidden by accident; add it to PASS_RATE_NOTES.
 * @param {Race} race
 */
export const passRateNote = (race) => {
  const rate = race.attributes.measurePassRate;
  if (!rate) return '';
  return PASS_RATE_NOTES[rate] ?? rate;
};

/**
 * How many candidates win ('Top two win'), or '' when there's nothing to show
 * @param {Race} race
 */
export const voteForNote = (race) => race.attributes.voteFor ?? '';

/**
 * The pass-rate and vote-for notes that have text, in display order
 * @param {Race} race
 */
export const raceNotes = (race) =>
  [passRateNote(race), voteForNote(race)].filter(Boolean);

/** @param {Race} race */
export const numberOfWinners = (race) => race.attributes.numberOfWinners ?? 1;

/** @param {Race} race */
export const getUpdatedDate = (race) => new Date(race.attributes.lastUpdated);

/** @param {Race[]} races */
export const latestUpdate = (races) =>
  new Date(Math.max(...races.map((r) => Date.parse(r.attributes.lastUpdated))));

/**
 * Latest update across U.S. House, Senate and governors in the TRENDS feed
 * @param {TrendsFeed} trends
 */
export const latestTrendsUpdate = (trends) => {
  const { house, senate, governor } = trends.data.attributes;
  return new Date(
    Math.max(...[house, senate, governor].map((c) => Date.parse(c.time)))
  );
};

/**
 * True once any U.S. House, Senate or governor race in the TRENDS feed is
 * leading or won
 * @param {TrendsFeed} trends
 */
export const trendsHaveResults = (trends) => {
  const { house, senate, governor } = trends.data.attributes;
  return [house, senate, governor].some((chamber) =>
    Object.values(chamber).some(
      (party) =>
        typeof party === 'object' && (party.won > 0 || party.leading > 0)
    )
  );
};

/** @param {Choice} choice */
export const isWinner = (choice) => choice.result === 'winner';

/** @param {Choice} choice */
export const isRunoff = (choice) => choice.result === 'runoff';

/**
 * 'yes' / 'no' for a measure choice (its choiceId), else null.
 * @param {Choice} choice
 * @returns {'yes' | 'no' | null}
 */
export const yesNo = (choice) =>
  choice.choiceId === 'yes' || choice.choiceId === 'no'
    ? choice.choiceId
    : null;

/**
 * Display label for a measure choice: 'Yes' / 'No'. AP sends 'Yes' / 'No' but
 * L.A. Vote sends 'YES' / 'NO', so this makes them match. Any other choice
 * gets its title as-is.
 * @param {Choice} choice
 */
export const measureLabel = (choice) => {
  const answer = yesNo(choice);
  if (answer === 'yes') return 'Yes';
  if (answer === 'no') return 'No';
  return choice.title ?? '';
};

/**
 * Party abbreviation, or 'yes' / 'no' for measures
 * @param {Choice} choice
 */
export const partyOf = (choice) =>
  choice.party?.abbr ?? yesNo(choice) ?? choice.choiceId;

/**
 * CSS party name the styles use: 'dem' | 'gop' | 'np' | ... | 'other',
 * or 'yes' / 'no' for measures
 * @param {Choice} choice
 */
export const partyClass = (choice) =>
  choice.party
    ? parties[choice.party.abbr]?.className ?? 'other'
    : yesNo(choice) ?? choice.choiceId;

/** @param {Choice} choice @param {Race} race */
export const votePct = (choice, race) =>
  race.attributes.totalVotes ? choice.votes / race.attributes.totalVotes : 0;

/**
 * A candidate's name parts, kept separate so components can show only the
 * last name where space is tight. Trimmed because the feed sometimes pads
 * them ('D. '); '' when missing. Measures have no parts, only title.
 * @param {Choice} choice
 */
export const nameParts = (choice) => ({
  first: choice.first?.trim() ?? '',
  middle: choice.middle?.trim() ?? '',
  last: choice.last?.trim() ?? '',
});

/**
 * One-string name for image alt text and slug classes only, not for display
 * or sorting: 'Lori D. Wilson', or 'Yes' / 'No' for measures.
 * @param {Choice} choice
 */
export const choiceName = (choice) => {
  if (choice.title) return choice.title;
  const { first, middle, last } = nameParts(choice);
  return [first, middle, last].filter(Boolean).join(' ');
};

/**
 * Candidates only (measures are ordered by yesFirst): last name, then first,
 * then middle.
 * @param {Choice} a @param {Choice} b
 */
const byName = (a, b) => {
  const A = nameParts(a);
  const B = nameParts(b);
  return (
    A.last.localeCompare(B.last) ||
    A.first.localeCompare(B.first) ||
    A.middle.localeCompare(B.middle)
  );
};

/** @param {Choice} a @param {Choice} b */
const yesFirst = (a, b) =>
  (yesNo(a) === 'yes' ? 0 : 1) - (yesNo(b) === 'yes' ? 0 : 1);

/**
 * Display order: until votes come in, measures are Yes then No and candidates
 * are alphabetical; after that, both are by votes. Ties keep the
 * before-results order (Yes first, or alphabetical).
 *
 * We've asked the data team whether the backend can send choices in this
 * order (not decided yet). If it does, delete this and render
 * attributes.choices as-is.
 * Alphabetical is by last name (see byName).
 * OPEN: whether ties should be alphabetical, and whether "no results yet"
 * should mean totalVotes === 0.
 * @param {Race} race
 */
export const orderedChoices = (race) => {
  const choices = [...race.attributes.choices];
  const beforeResults = isMeasure(race) ? yesFirst : byName;
  if (!race.attributes.totalVotes) return choices.sort(beforeResults);
  return choices.sort((a, b) => b.votes - a.votes || beforeResults(a, b));
};

/** 'Alameda County' → 'Alameda' @param {string} name */
export const shortCounty = (name) => name.replace(/ County$/, '').trim();

/**
 * Estimated votes left to count, from votes counted so far and the
 * estimated expected vote (EEVP) percentage. Returns 0 when EEVP is 0
 * (nothing reported) or 100 (fully counted), so callers that need to tell
 * those apart should check EEVP themselves.
 * @param {number} totalVotes
 * @param {number} eevp
 */
export const getVotesLeft = (totalVotes, eevp) => {
  const votes = Number(totalVotes);
  const percentIn = Number(eevp);

  if (
    !Number.isFinite(votes) ||
    votes < 0 ||
    !Number.isFinite(percentIn) ||
    percentIn <= 0 ||
    percentIn >= 100
  ) {
    return 0;
  }

  const estimatedTotalVotes = votes / (percentIn / 100);

  return Math.max(0, Math.round(estimatedTotalVotes - votes));
};

/**
 * A county's results ranked by votes, each joined to its race-level choice.
 * This is for the map's leader/margin math, not display order, so it always
 * ranks by votes. County choices only carry choiceId, so join on that unless
 * two candidates in the race share one, then fall back to array position.
 * @param {Race} race
 * @param {CountyResult} county
 * @returns {{choice: Choice, votes: number}[]}
 */
export const countyChoices = (race, county) => {
  const raceChoices = race.attributes.choices;
  const ids = raceChoices.map((c) => c.choiceId);
  const idsAreUnique = new Set(ids).size === ids.length;

  if (!idsAreUnique) {
    console.warn(
      `Duplicate choiceId in ${race.id}; joining county results by position`
    );
  }

  return county.choices
    .map((countyChoice, idx) => ({
      choice: idsAreUnique
        ? raceChoices.find((c) => c.choiceId === countyChoice.choiceId)
        : raceChoices[idx],
      votes: countyChoice.votes,
    }))
    .filter(
      /**
       * @param {{choice: Choice | undefined, votes: number}} d
       * @returns {d is {choice: Choice, votes: number}}
       */
      (d) => d.choice !== undefined
    )
    .sort((a, b) => b.votes - a.votes);
};
